import { useEffect, useState, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  User, Mail, Briefcase, Loader2, Plus, ListChecks,
  CalendarClock, Trash2, Clock, X, Pencil
} from 'lucide-react';
import {
  getDesignerProfile, updateDesignerProfile, addDesignerHours, deleteDesignerHours,
} from '@/lib/api';
import { adminListStaff, fmtErr } from '@/lib/staffApi';

/* ── Utils ─────────────────────────────────────────────────────── */
function todayISO() { return new Date().toISOString().slice(0, 10); }
function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
/** End time defaults to "now" only if it's after the start; otherwise mirrors start
 *  so the admin must consciously set a later end (avoids accidental overnight wrap). */
function smartEndTime(startHHMM) {
  const now = nowHHMM();
  return now > startHHMM ? now : startHHMM;
}
function fmtDate(iso) { return iso ? new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''; }
function fmtTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

/**
 * Designer Profile dialog — reads the project's `designer_user_id` reference,
 * looks the user up, and lets the admin attach a different staff user. Hours
 * are stored per-user; the dialog shows totals broken out by user.
 */
export function DesignerProfileDialog({ open, onOpenChange, projectId, shareToken, readOnly = false }) {
  const ctx = { projectId, shareToken };
  const adminPass = typeof window !== 'undefined' ? sessionStorage.getItem('app_password') || '' : '';
  const [data, setData] = useState({ designer: null, hours: [], total_hours: 0, totals_by_user: [] });
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [pickedUserId, setPickedUserId] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAddHours, setShowAddHours] = useState(false);
  const [showHoursLog, setShowHoursLog] = useState(false);

  const refresh = useCallback(async () => {
    if (!projectId && !shareToken) return;
    setLoading(true);
    try {
      const d = await getDesignerProfile(ctx);
      setData(d);
      setPickedUserId(d.designer?.id || '');
    } catch {
      toast.error('Failed to load designer profile');
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, shareToken]);

  // Load full staff list when admin opens — used to populate the picker.
  useEffect(() => {
    if (!open || !adminPass) return;
    adminListStaff(adminPass).then(s => setStaff(s.users || [])).catch(() => {});
  }, [open, adminPass]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const designerEligible = useMemo(() => staff.filter(u => u.role === 'designer'), [staff]);

  const save = async () => {
    setSaving(true);
    try {
      const d = await updateDesignerProfile(ctx, { user_id: pickedUserId || null });
      setData(d); setEditing(false);
      toast.success(pickedUserId ? 'Designer attached' : 'Designer cleared');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const addAdminUserToList = data.designer ? [data.designer] : [];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" data-testid="designer-profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />Designer Profile
            </DialogTitle>
            <DialogDescription>Attach a designer (from the staffing directory) and track hours for this project.</DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : editing ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5"><User className="h-3.5 w-3.5" />Designer</Label>
                <Select value={pickedUserId || '__none'} onValueChange={v => setPickedUserId(v === '__none' ? '' : v)}>
                  <SelectTrigger data-testid="designer-picker"><SelectValue placeholder="Pick a designer…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">No designer attached</SelectItem>
                    {designerEligible.length === 0 && <SelectItem value="__empty" disabled>No staff with role &apos;designer&apos; yet</SelectItem>}
                    {designerEligible.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1.5">Need a different designer? Add them in <a className="underline" href="/furniture-projects/staffing" target="_blank" rel="noreferrer">Staffing</a>.</p>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => { setEditing(false); setPickedUserId(data.designer?.id || ''); }}>Cancel</Button>
                <Button onClick={save} disabled={saving} data-testid="designer-save">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Save</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {data.designer ? (
                <div className="rounded-xl border border-border/70 bg-card p-4">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold flex-shrink-0">
                      {(data.designer.name || data.designer.email || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-base truncate" data-testid="designer-display-name">{data.designer.name || data.designer.email}</p>
                      <p className="text-xs text-muted-foreground capitalize mt-0.5 flex items-center gap-1"><Briefcase className="h-3 w-3" />{data.designer.role}</p>
                      <a href={`mailto:${data.designer.email}`} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1 mt-1" data-testid="designer-email"><Mail className="h-3 w-3" />{data.designer.email}</a>
                    </div>
                    {!readOnly && (
                      <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => setEditing(true)} data-testid="designer-edit">
                        <Pencil className="h-3 w-3" />Change
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-border/70 bg-muted/30 p-6 text-center">
                  <User className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">No designer attached yet.</p>
                  {!readOnly && <Button size="sm" onClick={() => setEditing(true)} data-testid="designer-add-info">Attach Designer</Button>}
                </div>
              )}

              {/* Total hours + actions */}
              <div className="rounded-xl bg-amber-50/60 border border-amber-200/70 p-4 flex items-center gap-3" data-testid="designer-hours-summary">
                <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0"><Clock className="h-5 w-5 text-amber-700" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-amber-700 font-medium">Total Hours · {data.totals_by_user?.length || 0} {data.totals_by_user?.length === 1 ? 'person' : 'people'}</p>
                  <p className="text-2xl font-heading font-semibold text-amber-900 tabular-nums" data-testid="designer-total-hours">
                    {data.total_hours.toFixed(2)}<span className="text-sm font-normal text-amber-700 ml-1.5">hrs</span>
                  </p>
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  {!readOnly && (
                    <Button size="sm" className="h-7 text-xs gap-1 bg-amber-600 hover:bg-amber-700" onClick={() => setShowAddHours(true)} data-testid="designer-add-hours-btn"><Plus className="h-3 w-3" />Add Hours</Button>
                  )}
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setShowHoursLog(true)} data-testid="designer-view-log-btn"><ListChecks className="h-3 w-3" />View Log</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AddHoursDialog open={showAddHours} onOpenChange={setShowAddHours} ctx={ctx} staff={staff} preselectedUserId={data.designer?.id || ''} extraStaff={addAdminUserToList} onAdded={setData} />
      <HoursLogDialog open={showHoursLog} onOpenChange={setShowHoursLog} ctx={ctx} data={data} onChange={setData} readOnly={readOnly} />
    </>
  );
}

/* ── Add Hours dialog — admin picks WHO the hours are for ───────── */
function AddHoursDialog({ open, onOpenChange, ctx, staff, preselectedUserId, extraStaff, onAdded }) {
  const [form, setForm] = useState({ user_id: '', date: todayISO(), start_time: '09:00', end_time: smartEndTime('09:00'), notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ user_id: preselectedUserId || '', date: todayISO(), start_time: '09:00', end_time: smartEndTime('09:00'), notes: '' });
  }, [open, preselectedUserId]);

  // Combine: full staff list + the currently-attached designer (in case
  // they were demoted to a different role later but still appear in legacy hours).
  const allUsers = useMemo(() => {
    const map = new Map();
    extraStaff?.forEach(u => u && map.set(u.id, { id: u.id, name: u.name, email: u.email, role: u.role }));
    staff.forEach(u => map.set(u.id, { id: u.id, name: u.name, email: u.email, role: u.role }));
    return Array.from(map.values());
  }, [staff, extraStaff]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.user_id) { toast.error('Pick whose hours these are.'); return; }
    if (!form.date || !form.start_time || !form.end_time) { toast.error('Date, start and end times are required.'); return; }
    setSaving(true);
    try {
      const updated = await addDesignerHours(ctx, form);
      onAdded(updated);
      toast.success('Hours logged');
      onOpenChange(false);
    } catch (err) { toast.error(fmtErr(err, 'Failed to add hours')); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-hours-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2"><Clock className="h-5 w-5 text-amber-600" />Add Hours</DialogTitle>
          <DialogDescription>Log time for any staff member working on this project.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5"><User className="h-3.5 w-3.5" />Whose hours?</Label>
            <Select value={form.user_id || '__none'} onValueChange={v => setForm(f => ({ ...f, user_id: v === '__none' ? '' : v }))}>
              <SelectTrigger data-testid="add-hours-user"><SelectValue placeholder="Pick a person…" /></SelectTrigger>
              <SelectContent>
                {allUsers.length === 0 && <SelectItem value="__none" disabled>No staff yet — add one in Staffing first</SelectItem>}
                {allUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email} <span className="text-muted-foreground">· {u.role}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" />Date</Label>
            <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required data-testid="add-hours-date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-muted-foreground mb-1 block">Start</Label><Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} required data-testid="add-hours-start" /></div>
            <div><Label className="text-xs text-muted-foreground mb-1 block">End</Label><Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} required data-testid="add-hours-end" /></div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Notes (optional)</Label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="What was worked on?" rows={3} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring resize-y" data-testid="add-hours-notes" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="add-hours-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}Log Hours</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── Hours Log dialog — list with per-user breakdown + total ────── */
function HoursLogDialog({ open, onOpenChange, ctx, data, onChange, readOnly }) {
  const [busyId, setBusyId] = useState(null);
  const remove = async (entryId) => {
    if (!window.confirm('Delete this hours entry?')) return;
    setBusyId(entryId);
    try { onChange(await deleteDesignerHours(ctx, entryId)); toast.success('Entry removed'); }
    catch { toast.error('Failed to remove entry'); }
    finally { setBusyId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col" data-testid="hours-log-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2"><ListChecks className="h-5 w-5 text-amber-600" />Hours Log</DialogTitle>
          <DialogDescription>All hours logged on this project, broken out by person.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-4">
          {/* Per-user summary chips */}
          {data.totals_by_user?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {data.totals_by_user.map(t => (
                <div key={t.user_id || 'legacy'} className="px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs" data-testid="hours-user-chip">
                  <span className="font-medium">{t.user_name}</span> · <span className="tabular-nums">{t.hours.toFixed(2)} hrs</span>
                </div>
              ))}
            </div>
          )}
          {data.hours.length === 0 ? (
            <div className="py-12 text-center"><Clock className="h-10 w-10 text-muted-foreground/40 mx-auto mb-2" /><p className="text-sm text-muted-foreground">No hours logged yet.</p></div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="hours-log-table">
                <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">User</th>
                    <th className="text-left px-3 py-2 font-medium">Date</th>
                    <th className="text-left px-3 py-2 font-medium">Time</th>
                    <th className="text-right px-3 py-2 font-medium">Hours</th>
                    <th className="text-left px-3 py-2 font-medium">Notes</th>
                    {!readOnly && <th className="px-3 py-2 w-8"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.hours.map(h => (
                    <tr key={h.id} className="hover:bg-muted/30" data-testid="hours-log-row">
                      <td className="px-3 py-2 whitespace-nowrap font-medium">{h.user_name || '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap" data-testid="hours-log-date">{fmtDate(h.date)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-muted-foreground text-xs">{fmtTime(h.start_time)} – {fmtTime(h.end_time)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium" data-testid="hours-log-hours">{Number(h.hours).toFixed(2)}</td>
                      <td className="px-3 py-2 text-muted-foreground text-xs whitespace-pre-wrap break-words max-w-xs">{h.notes || '—'}</td>
                      {!readOnly && (
                        <td className="px-3 py-2 text-right">
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" disabled={busyId === h.id} onClick={() => remove(h.id)} data-testid="hours-log-delete">
                            {busyId === h.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-amber-50/60 border-t border-amber-200">
                  <tr>
                    <td colSpan={3} className="px-3 py-2 text-right font-semibold text-amber-900">Total</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-amber-900" data-testid="hours-log-total">{data.total_hours.toFixed(2)}</td>
                    <td colSpan={readOnly ? 1 : 2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-3 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="gap-1"><X className="h-3.5 w-3.5" />Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
